import { pushGTMEvent } from 'utils/eventGTM';

interface EventBannerProps {
    el: HTMLElement;
    props: {
        secondaryCtaText?: string;
        eventDate?: string;
        eventTitle?: string;
    };
}

const EventBanner = ({ el, props }: EventBannerProps): () => void => {
    const elements = document.querySelectorAll('.event-banner');
    if (elements.length !== 1) {
        throw new Error('Switching off functionality for styleguide page');
    }

    const ctaWrapper = document.querySelector('.event-banner-cta-wrapper');
    const footer = document.querySelector('footer') || document.querySelector('[data-footer]');
    const footerAcknowledgement = document.querySelector('.footer-acknowledgement');
    const secondaryCta = document.querySelector('.cta-secondary');

    let isFixed = false;

    const updateFixedState = (shouldBeFixed: boolean): void => {
        if (window.innerWidth >= 1280) {
            // Remove fixed state on desktop
            if (isFixed) {
                ctaWrapper?.classList.remove('fixed');
                isFixed = false;
            }
            return;
        }

        if (shouldBeFixed && !isFixed) {
            ctaWrapper?.classList.add('fixed');
            isFixed = true;
        } else if (!shouldBeFixed && isFixed) {
            ctaWrapper?.classList.remove('fixed');
            isFixed = false;
        }
    };

    let shouldBeFixed = false;
    let footerInView = false;
    let acknowledgementInView = false;

    const checkAndUpdateFixedState = (): void => {
        // Only make it fixed if event banner is out of view AND neither footer nor acknowledgement is in view
        updateFixedState(shouldBeFixed && !footerInView && !acknowledgementInView);
    };

    let triggerObserver: IntersectionObserver | null = null;
    if (el) {
        triggerObserver = new IntersectionObserver(
            entries => {
                entries.forEach(entry => {
                    // When the bottom of event-banner leaves the viewport at the top, CTA should be fixed
                    // When the bottom of event-banner comes back into viewport from the top, CTA should not be fixed
                    shouldBeFixed = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
                    checkAndUpdateFixedState();
                });
            },
            {
                rootMargin: '0px',
                threshold: 0,
            }
        );
        triggerObserver.observe(el);
    }

    let footerObserver: IntersectionObserver | null = null;
    if (footer) {
        footerObserver = new IntersectionObserver(
            entries => {
                entries.forEach(entry => {
                    footerInView = entry.isIntersecting;
                    checkAndUpdateFixedState();
                });
            },
            {
                rootMargin: '0px',
                threshold: 0,
            }
        );
        footerObserver.observe(footer);
    }

    let acknowledgementObserver: IntersectionObserver | null = null;
    if (footerAcknowledgement) {
        acknowledgementObserver = new IntersectionObserver(
            entries => {
                entries.forEach(entry => {
                    acknowledgementInView = entry.isIntersecting;
                    checkAndUpdateFixedState();
                });
            },
            {
                rootMargin: '0px',
                threshold: 0,
            }
        );
        acknowledgementObserver.observe(footerAcknowledgement);
    }

    const handleResize = (): void => {
        // Check if we should update fixed state based on window width
        if (window.innerWidth >= 1280 && isFixed) {
            updateFixedState(false);
        }
    };

    const links = el.querySelectorAll('.event-banner-update');
    links.forEach(link => {
        link.addEventListener('click', e => {
            e.preventDefault(); // Ensures scroll behaviour works
            const targetId = link.getAttribute('href')?.substring(1);
            if (targetId) {
                const targetElement = document.getElementById(targetId);
                if (targetElement) {
                    const y = targetElement.getBoundingClientRect().top + window.scrollY;
                    window.scrollTo({ top: y, behavior: 'smooth' });
                }
            }
        });
    });

    window.addEventListener('resize', handleResize);

    const isTicketButton = props.secondaryCtaText?.toLowerCase() === 'tickets';

    if (secondaryCta && isTicketButton) {
        secondaryCta.addEventListener('click', () => {
            pushGTMEvent({
                actionType: 'buy_tickets',
                ev_name: props.eventTitle || '',
                ev_date: props.eventDate || '',
            });
        });
    }

    return () => {
        window.removeEventListener('resize', handleResize);
        if (triggerObserver) {
            triggerObserver.disconnect();
        }
        if (footerObserver) {
            footerObserver.disconnect();
        }
        if (acknowledgementObserver) {
            acknowledgementObserver.disconnect();
        }
    };
};

export default EventBanner;
